import {
  AlertCircle,
  Check,
  Clock,
  ExternalLink,
  Mail,
  RefreshCw,
} from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

import { trackConversion, trackEvent } from "../utils/analytics";
import { publicAnonKey } from "../utils/supabase/info";

import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";

interface EmailVerificationScreenProps {
  email: string;
  submissionId: string;
  onVerified: () => void;
  onVerifyLater: () => void;
  onBack: () => void;
  isPreVerified?: boolean;
}

export default function EmailVerificationScreen({
  email,
  submissionId,
  onVerified,
  onVerifyLater,
  onBack,
  isPreVerified = false,
}: EmailVerificationScreenProps) {
  const [verificationStatus, setVerificationStatus] = useState<
    "pending" | "verified" | "error"
  >("pending");
  const [error, setError] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [isCheckingManually, setIsCheckingManually] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Check if verification happened (much less aggressive polling)
  useEffect(() => {
    // If pre-verified, skip polling and show success immediately
    if (isPreVerified) {
      setVerificationStatus("verified");
      // Auto-redirect to home after 5 seconds
      setTimeout(() => {
        onVerified();
      }, 5000);
      return;
    }

    // Only do a gentle check once after 10 seconds, then every 30 seconds
    const timeouts: NodeJS.Timeout[] = [];

    // First check after 10 seconds (enough time for user to potentially click email)
    const firstCheck = setTimeout(() => {
      checkVerificationStatus(false);
    }, 10000);
    timeouts.push(firstCheck);

    // Then check every 30 seconds, but only a few times
    let checkCount = 0;
    const recurringCheck = setInterval(() => {
      if (checkCount < 4 && verificationStatus === "pending") {
        // Only check 4 times (2 minutes)
        checkVerificationStatus(false);
        checkCount++;
      } else {
        clearInterval(recurringCheck);
      }
    }, 30000);

    return () => {
      timeouts.forEach(clearTimeout);
      clearInterval(recurringCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStatus, isPreVerified]);

  const checkVerificationStatus = async (isManual = true) => {
    try {
      if (isManual) {
        setIsCheckingManually(true);
        setError("");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "CHECK_VERIFICATION_STATUS",
            email: email,
            submissionId: submissionId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to check verification status");
      }

      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }
      if (data.isVerified) {
        setVerificationStatus("verified");
        trackConversion("email_verified", "waitlist");
        trackEvent("email_verification_success", {
          event_category: "waitlist",
          verification_method: "email_link_click",
        });
        setTimeout(() => {
          onVerified();
        }, 5000); // 5 second delay before redirect to home
      }
      if (!data.isVerified) {
        setError(
          "Email has not been verified. Please try again. Check your spam folder, or try resending."
        );
        return;
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      if (isManual) {
        setError("Failed to check verification status. Please try again.");
      }
    } finally {
      if (isManual) {
        setIsCheckingManually(false);
      }
    }
  };

  const handleManualCheck = async () => {
    trackEvent("email_verification_manual_check", {
      event_category: "waitlist",
      email_domain: email.split("@")[1],
    });

    await checkVerificationStatus(true);
  };

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      setError("");
      setResendSuccess(false);

      trackEvent("email_verification_resend", {
        event_category: "waitlist",
        email_domain: email.split("@")[1],
      });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify({
            action: "RESEND_VERIFICATION_EMAIL",
            email: email,
            submissionId: submissionId,
          }),
        }
      );

      if (!response.ok) {
        setResendSuccess(false);
        setError("Failed to resend verification email. Please try again.");
        return;
      }
      const data = await response.json();
      if (data.error) {
        setError(data.error);
        return;
      }

      setResendSuccess(true);
    } catch (error) {
      console.error("Error resending email:", error);
      setError("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyLater = () => {
    trackEvent("email_verification_deferred", {
      event_category: "waitlist",
      email_domain: email.split("@")[1],
    });
    onVerifyLater();
  };

  const openEmailClient = () => {
    // Try to open the user's default email client
    window.location.href = `mailto:`;

    trackEvent("email_client_opened", {
      event_category: "waitlist",
      email_domain: email.split("@")[1],
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <Button variant="ghost" onClick={onBack} className="p-2">
          ←
        </Button>
        <h1 className="text-lg font-semibold text-primary">Verify Email</h1>
        <div className="w-10" /> {/* Spacer */}
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md space-y-6"
        >
          {/* Status Icon */}
          <div className="text-center">
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full glass mb-4"
              animate={
                verificationStatus === "verified" ? { scale: [1, 1.1, 1] } : {}
              }
              transition={{ duration: 0.5 }}
            >
              {verificationStatus === "verified" ? (
                <Check className="w-10 h-10 text-primary" />
              ) : (
                <Mail className="w-8 h-8 text-muted-foreground" />
              )}
            </motion.div>

            <h2 className="text-xl font-semibold mb-2">
              {verificationStatus === "verified"
                ? "Email Verified!"
                : "Check Your Email"}
            </h2>

            <p className="text-muted-foreground text-center leading-relaxed">
              {verificationStatus === "verified" ? (
                "Great! Your email has been verified. You're now confirmed on our waitlist with full benefits."
              ) : (
                <>
                  We sent a verification link to
                  <br />
                  <span className="font-medium text-foreground">{email}</span>
                </>
              )}
            </p>
          </div>

          {/* Verification Status */}
          {verificationStatus === "pending" && (
            <div className="glass-subtle rounded-lg p-4 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">
                    Waiting for verification...
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Click the verification link in the email we sent you to
                    become eligible for our early beta access and our lifetime
                    membership drawing. It should arrive within a few minutes.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Verification Success */}
          {verificationStatus === "verified" && (
            <motion.div
              className="glass-primary rounded-lg p-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center space-x-3">
                <Check className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium text-primary">
                    Verification Complete
                  </p>
                  <p className="text-sm text-primary/80">
                    {isPreVerified
                      ? "Your email was verified successfully! You're now confirmed on our waitlist."
                      : "You're eligible for early beta access and our lifetime membership drawing"}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert className="border-destructive/20 bg-destructive/10">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Resend Success */}
          {resendSuccess && (
            <Alert className="border-primary/20 bg-primary/10">
              <Check className="h-4 w-4 text-primary" />
              <AlertDescription className="text-primary">
                Verification email sent! Check your inbox and spam folder.
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {verificationStatus !== "verified" && (
              <>
                {/* Manual Check Button */}
                <Button
                  onClick={handleManualCheck}
                  disabled={isCheckingManually}
                  variant="default"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {isCheckingManually ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Check if I&apos;ve Verified
                    </>
                  )}
                </Button>

                {/* Open Email Client Button */}
                <Button
                  onClick={openEmailClient}
                  variant="outline"
                  className="w-full glass-hover border-border/50"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Email App
                </Button>

                {/* Resend Button */}
                <Button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  variant="outline"
                  className="w-full glass-hover border-border/50"
                >
                  {isResending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Resending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>

                {/* Verify Later Button */}
                <Button
                  onClick={handleVerifyLater}
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                >
                  I&apos;ll Verify Later
                </Button>
              </>
            )}
          </div>

          {/* Help Text */}
          {verificationStatus === "pending" && (
            <div className="text-center text-xs text-muted-foreground space-y-2">
              <div className="bg-muted/20 rounded-lg p-3 space-y-1">
                <p className="font-medium">Can&apos;t find the email?</p>
                <p>• Check your spam/junk folder</p>
                <p>• Make sure {email} is correct</p>
                <p>• Try the &quot;Resend&quot; button above</p>
              </div>
              <p className="text-xs opacity-75">
                The verification link will expire in 24 hours
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
